import { ZOOBOOK_VERSION } from '@zoobook/shared/config/versions';
import { createClient as createWSClient } from 'graphql-ws';
import { Client, cacheExchange, fetchExchange, subscriptionExchange } from 'urql';
import { getSupabase } from '../supabase/client';

const graphqlUrl = process.env.GRAPHQL_URL || 'http://localhost:5678/graphql';
const graphqlWsUrl = process.env.GRAPHQL_WS_URL || 'ws://localhost:5678/graphql';

/**
 * Cached access token — updated by setAccessToken() from the auth layer.
 * This avoids fragile localStorage parsing in the synchronous fetchOptions callback.
 */
let _cachedAccessToken: string | null = null;

/**
 * Set the current access token for GraphQL requests.
 * Called from AuthProvider when session changes.
 */
export function setAccessToken(token: string | null): void {
  _cachedAccessToken = token;
}

/**
 * Extract access_token from a Supabase auth localStorage entry.
 * Handles multiple storage formats across @supabase/auth-js versions:
 *   - v2 early: raw Session object { access_token, refresh_token, ... }
 *   - v2 late:  wrapped { currentSession: Session, expiresAt: number }
 *   - v2.64+:  base64-encoded chunks (sb-<ref>-auth-token.0, .1, etc.)
 */
function extractTokenFromStorageValue(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw);
    // Direct session format: { access_token: "..." }
    if (typeof parsed?.access_token === 'string') return parsed.access_token;
    // Wrapped format: { currentSession: { access_token: "..." } }
    if (typeof parsed?.currentSession?.access_token === 'string')
      return parsed.currentSession.access_token;
    // Nested session format: { session: { access_token: "..." } }
    if (typeof parsed?.session?.access_token === 'string') return parsed.session.access_token;
  } catch {
    // Not JSON — ignore
  }
  return null;
}

/**
 * Fallback: scan localStorage for Supabase auth token.
 * Used when the cached token is not yet set (e.g., page reload before AuthProvider mounts).
 */
function getTokenFromLocalStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // First try chunked storage format (supabase-js v2.64+)
    // Keys: sb-<ref>-auth-token.0, sb-<ref>-auth-token.1, etc.
    const chunks: string[] = [];
    let chunkBase: string | null = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('sb-') && key.includes('-auth-token')) {
        if (key.match(/-auth-token\.\d+$/)) {
          if (!chunkBase) chunkBase = key.replace(/\.\d+$/, '');
          chunks.push(key);
        } else if (key.endsWith('-auth-token')) {
          // Non-chunked: try direct parse
          const raw = localStorage.getItem(key);
          if (raw) {
            const token = extractTokenFromStorageValue(raw);
            if (token) return token;
          }
        }
      }
    }
    // Reassemble chunked storage
    if (chunkBase && chunks.length > 0) {
      chunks.sort(); // .0, .1, .2 ...
      const combined = chunks.map((k) => localStorage.getItem(k) ?? '').join('');
      if (combined) {
        const token = extractTokenFromStorageValue(combined);
        if (token) return token;
      }
    }
  } catch (err) {
    console.warn('[GraphQL] Failed to read token from localStorage:', err);
  }
  return null;
}

const wsClient =
  typeof window !== 'undefined'
    ? createWSClient({
        url: graphqlWsUrl,
        connectionParams: async () => {
          const client = getSupabase();
          if (client) {
            const { data } = await client.auth.getSession();
            return {
              authorization: data.session?.access_token
                ? `Bearer ${data.session.access_token}`
                : '',
            };
          }
          // Fallback to cached token
          const token = _cachedAccessToken ?? getTokenFromLocalStorage();
          return { authorization: token ? `Bearer ${token}` : '' };
        },
      })
    : null;

export const graphqlClient = new Client({
  url: graphqlUrl,
  preferGetMethod: false,
  exchanges: [
    cacheExchange,
    fetchExchange,
    ...(wsClient
      ? [
          subscriptionExchange({
            forwardSubscription: (request) => ({
              subscribe: (sink) => ({
                unsubscribe: wsClient.subscribe(
                  { query: request.query as string, variables: request.variables },
                  // biome-ignore lint/suspicious/noExplicitAny: graphql-ws sink type is incompatible with urql's expected type
                  sink as any,
                ),
              }),
            }),
          }),
        ]
      : []),
  ],
  fetchOptions: () => {
    // Primary: use cached token set by AuthProvider (most reliable)
    // Fallback: scan localStorage for Supabase auth session (page reload before AuthProvider mounts)
    const token = _cachedAccessToken ?? getTokenFromLocalStorage();
    if (!token) {
      console.warn('[GraphQL] No auth token available — request will be unauthenticated');
    }
    return {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        'X-Zoobook-Client': `web/${ZOOBOOK_VERSION}`,
      },
    };
  },
});
